import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, useTheme } from '@mui/material';
import { ShieldCheck, Mail, Phone, Trash2 } from 'lucide-react';

export type VerificationMethod = 'email' | 'phone' | 'both';

interface ValidateCustomerNodeData {
  verificationMethod: VerificationMethod;
  onDelete?: () => void;
}

const getVerificationIcon = (method: VerificationMethod) => {
  switch (method) {
    case 'email':
      return <Mail size={16} />;
    case 'phone':
      return <Phone size={16} />;
    case 'both':
      return <ShieldCheck size={16} />;
    default:
      return <ShieldCheck size={16} />;
  }
};

const getVerificationLabel = (method: VerificationMethod): string => {
  switch (method) {
    case 'email':
      return 'Email Verification';
    case 'phone':
      return 'SMS Verification';
    case 'both':
      return 'Email & SMS Verification';
    default:
      return 'Customer Verification';
  }
};

const getVerificationDescription = (method: VerificationMethod): string => {
  switch (method) {
    case 'email':
      return 'Customer will verify via email OTP';
    case 'phone':
      return 'Customer will verify via SMS OTP';
    case 'both':
      return 'Customer will verify via email or SMS';
    default:
      return 'Validates customer identity before form submission';
  }
};

const ValidateCustomerNode: React.FC<NodeProps<ValidateCustomerNodeData>> = ({ data }) => {
  const theme = useTheme();
  const verificationMethod = data?.verificationMethod || 'email';

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (data?.onDelete) {
      data.onDelete();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${theme.palette.warning.main}`,
        borderRadius: 2,
        minWidth: 280,
        boxShadow: theme.shadows[2],
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: theme.palette.warning.main,
          width: 8,
          height: 8,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          backgroundColor: theme.palette.warning.main,
          borderRadius: '6px 6px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldCheck size={18} color="#fff" />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
            Validate Customer
          </Typography>
        </Box>
        <Box
          className="nodrag nopan"
          onClick={handleDelete}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.8)',
            '&:hover': {
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <Trash2 size={14} />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            color: theme.palette.warning.dark,
          }}
        >
          {getVerificationIcon(verificationMethod)}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getVerificationLabel(verificationMethod)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {getVerificationDescription(verificationMethod)}
        </Typography>
      </Box>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: theme.palette.warning.main,
          width: 8,
          height: 8,
        }}
      />
    </Box>
  );
};

export default ValidateCustomerNode;
